import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, TriangleAlert } from "lucide-react";
import { ContactFooter } from "@/components/ContactFooter";

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | paid | expired | error | manual
  const pollsRef = useRef(0);
  const isLogged = Boolean(user || localStorage.getItem("token"));

  useEffect(() => {
    if (!sessionId) {
      // Ritorno da Payment Link senza id sessione: verifica manuale (admin) o automatica lato server
      setState("manual");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      pollsRef.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setState("paid");
          if (localStorage.getItem("token")) {
            try {
              await refreshUser();
            } catch {
              // sessione non più valida: l'accesso è comunque sbloccato lato server
            }
          }
          return;
        }
        if (r.data.status === "unverified") {
          setState("manual");
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setState("expired");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      if (pollsRef.current >= MAX_POLLS) {
        setState("error");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  // CTA adattiva: se non c'è una sessione attiva, porta al login
  const primaryTo = isLogged ? "/" : "/login";
  const primaryLabel = isLogged ? "Inizia ad allenarti" : "Accedi per iniziare";

  return (
    <div className="min-h-screen bg-white">
      <header className="max-w-3xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--volt)] border border-black/10" aria-hidden="true" />
          <span className="font-extrabold tracking-tight uppercase text-sm">Propedeutico</span>
        </div>
      </header>
      <div className="max-w-md mx-auto px-4 pt-14 pb-16 text-center fade-up">
        {state === "verifying" && (
          <div data-testid="payment-verifying">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[color:var(--text-2)]" aria-hidden="true" />
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight">Verifica del pagamento…</h1>
            <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
              Un attimo: stiamo confermando la transazione con Stripe. Non chiudere questa pagina.
            </p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-success">
            <span className="inline-flex w-14 h-14 rounded-full bg-[color:var(--volt)] items-center justify-center" aria-hidden="true">
              <Check className="w-7 h-7" strokeWidth={3} />
            </span>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight">Accesso sbloccato.</h1>
            <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
              Pagamento confermato. Da ora hai tutte le simulazioni, le lezioni e il ripasso errori. In bocca al lupo per l'esame.
            </p>
            <Link
              to={primaryTo}
              data-testid="payment-success-home-button"
              className="press mt-6 inline-block rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
            >
              {primaryLabel}
            </Link>
          </div>
        )}

        {state === "manual" && (
          <div data-testid="payment-manual">
            <span className="inline-flex w-14 h-14 rounded-full bg-[color:var(--volt)] items-center justify-center" aria-hidden="true">
              <Check className="w-7 h-7" strokeWidth={3} />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight">Grazie! Pagamento in elaborazione.</h1>
            <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
              Se hai completato il pagamento, il tuo accesso completo verrà attivato a breve.
              Se dopo qualche minuto non risulta attivo, esci e rientra nell'app oppure contatta l'assistenza indicando l'email con cui hai pagato.
            </p>
            <Link
              to={primaryTo}
              data-testid="payment-manual-home-button"
              className="press mt-6 inline-block rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
            >
              {isLogged ? "Torna alla home" : "Accedi all'app"}
            </Link>
          </div>
        )}

        {(state === "expired" || state === "error") && (
          <div data-testid="payment-error">
            <span className="inline-flex w-14 h-14 rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] items-center justify-center" aria-hidden="true">
              <TriangleAlert className="w-6 h-6 text-[color:var(--danger)]" />
            </span>
            <h1 className="mt-5 text-2xl font-extrabold tracking-tight">
              {state === "expired" ? "Sessione scaduta." : "Verifica non riuscita."}
            </h1>
            <p className="mt-2 text-sm text-[color:var(--text-2)] leading-6">
              {state === "expired"
                ? "La sessione di pagamento è scaduta o non è andata a buon fine. Nessun importo è stato addebitato."
                : "Non riusciamo a confermare il pagamento in questo momento. Se hai completato il pagamento, l'accesso verrà attivato automaticamente a breve."}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link
                to={isLogged ? "/sblocca" : "/login"}
                data-testid="payment-retry-button"
                className="press rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
              >
                {isLogged ? "Riprova" : "Accedi"}
              </Link>
              <Link
                to={primaryTo}
                data-testid="payment-error-home-button"
                className="press rounded-full border border-[color:var(--hairline)] font-bold px-6 py-3 min-h-[44px] hover:bg-[color:var(--bg-subtle)] transition-colors duration-200"
              >
                Home
              </Link>
            </div>
          </div>
        )}
        <div className="flex justify-center">
          <ContactFooter className="pb-10" />
        </div>
      </div>
    </div>
  );
}
